import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCurrent } from "../../hooks";
import usePost from "../../hooks/usePost";
import SelectorTree from "../SelectAccount/SelectAccount/SelectorTree";

const MwSelectorAccount = ({
  onChangeCallback,
  withLabel = false,
  invalid = false,
  initialValue = "",
  disabled = false,
  placeholder = "",
  classNamePrivate = "",
  allowPrimaryAccounts = false, // New prop: default false = only secondary accounts
  showNumberField = true, // New prop: show/hide number field
  treasuryAccount = null, // New prop: treasury account object {account_no, name_ar, name_en}
  levelLimit = null, // New prop: limit tree to specific level (null = no limit)
}) => {
  const [t] = useTranslation("global");
  const { currentLangId, currentTenantId, currentCompanyId } = useCurrent();
  const [accountsData, setAccountsData] = useState([]);
  const hasFetched = useRef(false);
  
  // Parse initial value to get account number and name
  const parseInitialValue = (value) => {
    if (!value) return { account_no: "", account_name: "" };
    
    // If it's a string, check if it contains "/" (old format)
    if (typeof value === 'string') {
      const parts = value.split("/");
      if (parts.length >= 2) {
        return {
          account_no: parts[1]?.trim() || "",
          account_name: parts[0]?.trim() || "",
        };
      }
      // Otherwise, treat it as account_no only (new format)
      return {
        account_no: value.trim(),
        account_name: "", // Name will be found from tree data
      };
    }
    
    // If it's an object, return as is
    if (typeof value === 'object' && value !== null) {
      return {
        account_no: value.account_no || "",
        account_name: value.name_ar || value.name_en || "",
      };
    }
    
    return { account_no: "", account_name: "" };
  };

  const initialParsed = parseInitialValue(initialValue);
  const [accountNo, setAccountNo] = useState(initialParsed.account_no);
  const [accountName, setAccountName] = useState(initialParsed.account_name);
  const [lastProcessedTreasuryAccount, setLastProcessedTreasuryAccount] = useState(null);
  const hasTreasuryBeenSet = useRef(false); // Track if treasury was ever set

  // استدعاء API لجلب شجرة الحسابات
  const { data, loading, postData } = usePost();

  // جلب البيانات عند تحميل المكون (مرة واحدة فقط)
  useEffect(() => {
    if (currentTenantId && currentCompanyId && !hasFetched.current) {
      hasFetched.current = true;
      postData("https://api.absat-erp.com/acc/tree-account", {
        tenant_id: currentTenantId,
        company_id: currentCompanyId,
      });
    }
  }, [currentTenantId, currentCompanyId, postData]);

  // تحديث القيم عند تغيير initialValue (في وضع التعديل)
  useEffect(() => {
    const parsed = parseInitialValue(initialValue);
    setAccountNo(parsed.account_no || "");
    setAccountName(parsed.account_name || "");
  }, [initialValue]);

  // تحديث accountsData عند وصول البيانات
  useEffect(() => {
    let rawData = [];
    if (data?.data?.treeParent) {
      rawData = data.data.treeParent;
    } else if (data?.data && Array.isArray(data.data)) {
      // Fallback: if data exists as array
      rawData = data.data;
    }

    // Add level information to each node if levelLimit is provided
    if (levelLimit && rawData.length > 0) {
      const addLevelInfo = (nodes, allowedLevel) => {
        if (!Array.isArray(nodes)) return [];
        
        return nodes.map(node => ({
          ...node,
          allowedLevel: allowedLevel, // Store the allowed level
          childs: node.childs && node.childs.length > 0
            ? addLevelInfo(node.childs, allowedLevel)
            : []
        }));
      };
      
      const dataWithLevelInfo = addLevelInfo(rawData, levelLimit);
      setAccountsData(dataWithLevelInfo);
    } else {
      setAccountsData(rawData);
    }
  }, [data, levelLimit]);

  // دالة للبحث عن اسم الحساب باستخدام account_no في الشجرة
  const findAccountNameByNumber = (accountNo, tree) => {
    if (!accountNo || !tree || !Array.isArray(tree)) return "";
    
    for (const node of tree) {
      // البحث في العقدة الحالية
      if (String(node.account_no) === String(accountNo)) {
        const name = currentLangId === "ar" 
          ? (node.name_ar || node.name_en || "")
          : (node.name_en || node.name_ar || "");
        return name;
      }
      
      // البحث في الأبناء (childs)
      if (node.childs && Array.isArray(node.childs)) {
        const foundName = findAccountNameByNumber(accountNo, node.childs);
        if (foundName) return foundName;
      }
    }
    
    return "";
  };

  // عند تحميل بيانات الشجرة، حاول إيجاد الاسم إذا كان accountNo موجوداً
  useEffect(() => {
    if (accountNo && accountsData.length > 0) {
      const foundName = findAccountNameByNumber(accountNo, accountsData);
      setAccountName(foundName || "");
      return;
    }
    if (!accountNo) {
      setAccountName("");
    }
  }, [accountNo, accountsData, currentLangId]);

  // تحديث القيم عند تغيير treasuryAccount (حساب الخزينة الافتراضي)
  // عندما يتم اختيار خزينة ولها حساب فرعي، يتم ملء الحقل تلقائياً
  // عندما يتم اختيار خزينة بدون حساب، يتم إفراغ الحقل
  useEffect(() => {
    if (treasuryAccount) {
      // فقط عيّن hasTreasuryBeenSet إذا كان treasury لديه account_no حقيقي
      if (treasuryAccount.account_no) {
        hasTreasuryBeenSet.current = true;
        
        // تجنب تكرار الاستدعاء: تحقق مما إذا كانت هذه خزينة مختلفة عن السابقة
        const treasuryKey = `${treasuryAccount.id}_${treasuryAccount.account_no}`;
        if (lastProcessedTreasuryAccount === treasuryKey) {
          return; // تم معالجتها بالفعل
        }
        
        setLastProcessedTreasuryAccount(treasuryKey);
        
        // تحديث رقم الحساب
        setAccountNo(treasuryAccount.account_no);
        
        // ابحث عن اسم الحساب في شجرة الحسابات
        if (accountsData.length > 0) {
          const foundName = findAccountNameByNumber(treasuryAccount.account_no, accountsData);
          if (foundName) {
            setAccountName(foundName);
          }
        }
        
        // استدعاء الـ callback مع بيانات الحساب المختار
        onChangeCallback({
          account_no: treasuryAccount.account_no,
          name_ar: treasuryAccount.name_ar || "",
          name_en: treasuryAccount.name_en || "",
          raw: treasuryAccount,
        });
      } else {
        // treasury موجود لكن بدون account_no - لا تفعل شيئاً، احتفظ بالقيمة الحالية
        const treasuryKey = `${treasuryAccount.id}_no_account`;
        if (lastProcessedTreasuryAccount !== treasuryKey) {
          setLastProcessedTreasuryAccount(treasuryKey);
        }
      }
    } else if (hasTreasuryBeenSet.current) {
      // فقط إذا تم تعيين treasury من قبل ثم تم إلغاءه، قم بإفراغ الحقل
      // لا تفعل شيئاً إذا لم يتم تعيين treasury أبداً (حالة التعديل مع initialValue)
      const treasuryKey = "no_treasury";
      if (lastProcessedTreasuryAccount !== treasuryKey) {
        setLastProcessedTreasuryAccount(treasuryKey);
        setAccountNo("");
        setAccountName("");
        onChangeCallback({
          account_no: "",
          name_ar: "",
          name_en: "",
          raw: null,
        });
      }
    } else {
      // treasury هو null ولم يتم تعيينه أبداً، لا تفعل شيئاً
    }
  }, [treasuryAccount, accountsData]);

  const handleSelect = (selected) => {
    setAccountNo(selected.account_no);
    setAccountName(selected.name);
    onChangeCallback(selected);
  };

  const handleManualNumberChange = (value) => {
    setAccountNo(value);
    // When manual number changes, we might want to update callback
    onChangeCallback({
      account_no: value,
      name_ar: "",
      name_en: "",
      raw: { account_no: value },
    });
  };

  return (
    <div>
      {withLabel && (
        <label className="text-xs text-[#251658] font-[700]">
          {t("invoice.selectAccount")}
        </label>
      )}

      <SelectorTree
        value={accountNo}
        label={accountName}
        title={currentLangId === "ar" ? "دليل الحسابات" : "Accounts Guide"}
        headerTitle={withLabel ? "" : t("invoice.selectAccount")}
        onSelect={handleSelect}
        onManualNumberChange={handleManualNumberChange}
        data={accountsData.length > 0 ? accountsData : data?.data?.treeParent || []}
        placeholderNumber={currentLangId === "ar" ? "رقم الحساب" : "Account Number"}
        placeholderName={currentLangId === "ar" ? "اسم الحساب" : "Account Name"}
        disabled={disabled}
        allowManualNumberInput={true}
        invalid={invalid}
        invalidMsg={invalid ? (currentLangId === "ar" ? "مطلوب" : "Required") : ""}
        allowedAccountTypes={allowPrimaryAccounts ? [] : ["secondary"]}
        showNumberField={showNumberField}
        withInlineSuggestions={true}
      />
    </div>
  );
};

export default MwSelectorAccount;
